import {ConfigObjectType, getLocalStorage, useApiQuery, useCurrentLocale} from '@wallaby-ui/core';
import {HaidiSchema, NormalizedOption, WallabyCoreSchema} from '@wallaby-ui/document';
import lodash from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';

export type StandardType = 'NO_TYPES' | 'ECDC';

// NOTE: cache the instance-wide standard type so consumers (e.g. the "HAI type detail" report filter,
// which is hidden while standardType === 'NO_TYPES') can render it on first paint instead of waiting for
// the `hai-standard-type` request. The refetch below still corrects the value if it changed (PDH-2840).
const STANDARD_TYPE_STORAGE_KEY = 'haidi.standardType';

export type HaiStandardTypeDto = WallabyCoreSchema.components['schemas']['HaiStandardTypeDto'];

export type CodeToTranslationsMap = {[key: string]: string};

export type UseStandardTypesProps = {
  config: ConfigObjectType;
  selectedType?: HaidiSchema.components['schemas']['HaiTypeEnum'] | undefined;
};

export function useStandardTypes({config, selectedType}: UseStandardTypesProps) {
  const [standardType, setStandardType] = useState<StandardType>(() => getLocalStorage<StandardType>(STANDARD_TYPE_STORAGE_KEY, 'NO_TYPES'));
  const [standardTypes, setStandardTypes] = useState<HaiStandardTypeDto[]>([]);
  const [standardTypesSortedBySelectedType, setStandardTypesSortedBySelectedType] = useState<HaiStandardTypeDto[]>([]);
  const [standardTypeTranslations, setStandardTypeTranslations] = useState<CodeToTranslationsMap>({});
  const [separatedStandardTypeCode, setSeparatedStandardTypeCode] = useState<string | null>(null);

  const standardTypeUrl = config.service.url.standardType;
  const {isLoading: standardTypeLoading, refetch: refetchStandardType} = useApiQuery<StandardType>([standardTypeUrl], null, {
    axiosConfig: {
      url: standardTypeUrl,
    },
    reactQueryOptions: {
      staleTime: 0,
      refetchOnMount: 'always',
      onSuccess: (type) => {
        setStandardType(type);
        localStorage.setItem(STANDARD_TYPE_STORAGE_KEY, JSON.stringify(type));
      },
      onError: () => setStandardType('NO_TYPES'),
    },
  });

  const emptyStandardTypesReplace: HaiStandardTypeDto[] = [
    {id: 1, code: 'UTI_PARENT', codeLabel: 'UTI_PARENT_LABEL', parent: undefined, haiTypeMapping: 'UTI'},
    {id: 2, code: 'UTI_CHILD_1', codeLabel: 'UTI_CHILD_1_LABEL', parent: 'UTI_PARENT', haiTypeMapping: 'SSI'},
    {id: 3, code: 'UTI_CHILD_2', codeLabel: 'UTI_CHILD_2_LABEL', parent: 'UTI_PARENT', haiTypeMapping: 'UTI'},
    {id: 4, code: 'SSI_PARENT', codeLabel: 'SSI_PARENT_LABEL', parent: undefined, haiTypeMapping: 'SSI'},
    {id: 5, code: 'SSI_CHILD_1', codeLabel: 'SSI_CHILD_1_LABEL', parent: 'SSI_PARENT', haiTypeMapping: 'SSI'},
    {id: 6, code: 'SSI_CHILD_2', codeLabel: 'SSI_CHILD_2_LABEL', parent: 'SSI_PARENT', haiTypeMapping: 'SSI'},
  ];
  const standardTypesListUrl = config.service.url.standardTypeList;
  const {isLoading: standardTypesLoading, refetch: refetchStandardTypes} = useApiQuery<HaiStandardTypeDto[]>([standardTypesListUrl], null, {
    axiosConfig: {
      url: standardTypesListUrl,
    },
    reactQueryOptions: {
      staleTime: 0,
      refetchOnMount: 'always',
      onSuccess: (data) => setStandardTypes(!data || data.length === 0 ? emptyStandardTypesReplace : data),
      onError: () => setStandardTypes(emptyStandardTypesReplace),
    },
  });

  useEffect(() => {
    if (!selectedType) {
      setStandardTypesSortedBySelectedType(standardTypes);
      return;
    }
    let lastFirstItemCode: string | null = null;

    const firstParentCodesToBeAdded: string[] = [];
    const lastParentCodesToBeAdded: string[] = [];
    const firstItemCodes: string[] = [];
    const lastItemCodes: string[] = [];
    lodash.forEachRight(standardTypes, (item) => {
      if (item.haiTypeMapping === selectedType || firstParentCodesToBeAdded.includes(item.code)) {
        firstItemCodes.unshift(item.code);
        if (lastFirstItemCode == null) {
          lastFirstItemCode = `${item.parent || item.code}|disc1`;
        }
        if (item.parent != null) {
          firstParentCodesToBeAdded.push(item.parent);
        }
      }
      if (item.haiTypeMapping !== selectedType || lastParentCodesToBeAdded.includes(item.code)) {
        lastItemCodes.unshift(item.code);
        if (item.parent != null) {
          lastParentCodesToBeAdded.push(item.parent);
        }
      }
    });

    const preferred = standardTypes
      .filter((i) => firstItemCodes.includes(i.code))
      .map((item) => ({...item, code: `${item.code}|disc1`, parent: item.parent ? `${item.parent}|disc1` : undefined}));
    const others = standardTypes
      .filter((i) => lastItemCodes.includes(i.code))
      .map((item) => ({...item, code: `${item.code}|disc2`, parent: item.parent ? `${item.parent}|disc2` : undefined}));

    setStandardTypesSortedBySelectedType([...preferred, ...others]);
    setSeparatedStandardTypeCode(lastFirstItemCode);
  }, [selectedType, standardTypes]);

  const language = (useCurrentLocale() as string).toUpperCase();

  const emptyStandardTypeTranslationsReplace: CodeToTranslationsMap = {
    UTI_PARENT: 'UTI parent',
    UTI_CHILD_1: 'UTI child 1 UTI child 2 UTI child 3 UTI child 4 UTI child 5 UTI child 6 UTI child 7 UTI child 8',
    UTI_CHILD_2: 'UTI child 2',
    SSI_PARENT: 'SSI parent',
    SSI_CHILD_1: 'SSI child 1',
    SSI_CHILD_2: 'SSI child 2',
  };
  const standardTypeTranslationsUrl = config.service.url.standardTypeTranslations;
  const {isLoading: standardTypeTranslationsLoading, refetch: refetchStandardTypeTranslations} = useApiQuery<CodeToTranslationsMap>(
    [standardTypeTranslationsUrl, language],
    null,
    {
      axiosConfig: {
        url: standardTypeTranslationsUrl,
        params: {language},
      },
      reactQueryOptions: {
        staleTime: 0,
        refetchOnMount: 'always',
        onSuccess: (data) => setStandardTypeTranslations(lodash.isEmpty(data) ? emptyStandardTypeTranslationsReplace : data),
        onError: () => setStandardTypeTranslations(emptyStandardTypeTranslationsReplace),
      },
    }
  );

  const isLoading = standardTypeLoading || standardTypeTranslationsLoading || standardTypesLoading;

  const standardTypeOptions = useMemo<NormalizedOption<HaiStandardTypeDto>[]>(
    () =>
      (standardTypesSortedBySelectedType || []).map((type) => {
        const label = `${type.codeLabel} - ${standardTypeTranslations[type.code.replace('|disc1', '').replace('|disc2', '')]}`;
        return {
          id: type.code,
          label,
          parentId: type.parent ?? undefined,
          original: type,
        };
      }),
    [standardTypeTranslations, standardTypesSortedBySelectedType]
  );

  const standardTypeOptionsUnsorted = useMemo<NormalizedOption<HaiStandardTypeDto>[]>(
    () =>
      (standardTypes || []).map((type) => ({
        id: type.code,
        label: `${type.codeLabel} - ${standardTypeTranslations[type.code]}`,
        parentId: type.parent ?? undefined,
        original: type,
      })),
    [standardTypeTranslations, standardTypes]
  );

  const getHaiTypeMappingForCode = useCallback(
    (standardTypeCode: string) => standardTypeOptions.find((option) => option.id === standardTypeCode)?.original?.haiTypeMapping,
    [standardTypeOptions]
  );

  const getParentHaiTypeMappingForCode = (standardTypeCode: string) => {
    let parentHaiTypeMapping: string | undefined;
    let option = standardTypeOptions.find((option) => option.id === standardTypeCode)?.original;
    if (option) {
      parentHaiTypeMapping = option.haiTypeMapping;
      if (option.parent) {
        option = standardTypeOptions.find((op) => op.id === option?.parent)?.original;
        parentHaiTypeMapping = option?.haiTypeMapping;
      }
    }
    return parentHaiTypeMapping;
  };

  const getCodeLabelForCode = (standardTypeCode: string) =>
    standardTypeOptions.find((option) => option.id === standardTypeCode)?.original?.codeLabel;

  const getTranslationForStandardTypeId = useCallback(
    (standardTypeId: number) => {
      const code = standardTypes.find((entity) => entity.id === standardTypeId)?.code as string;
      return standardTypeTranslations[code];
    },
    [standardTypeTranslations, standardTypes]
  );

  const getCodeLabelForStandardTypeId = useCallback(
    (standardTypeId: number) => {
      return standardTypes.find((entity) => entity.id === standardTypeId)?.codeLabel as string;
    },
    [standardTypes]
  );

  const getStandardTypeIdForCode = useCallback(
    (standardTypeCode: string) => {
      const cleanCode = standardTypeCode.replace('|disc1', '').replace('|disc2', '');
      return standardTypes.find((entity) => entity.code === cleanCode)?.id;
    },
    [standardTypes]
  );

  return {
    isLoading,
    standardType,
    standardTypes: standardTypesSortedBySelectedType,
    standardTypeOptions,
    standardTypeOptionsUnsorted,
    separatedStandardTypeCode,
    getHaiTypeMappingForCode,
    getCodeLabelForCode,
    getTranslationForStandardTypeId,
    standardTypeTranslations,
    getCodeLabelForStandardTypeId,
    getParentHaiTypeMappingForCode,
    getStandardTypeIdForCode,
  };
}
